import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Loader2, X } from 'lucide-react'
import { ArticleRenderer } from '../components/article-viewer/ArticleRenderer'
import Navbar from '../components/navbar'
import type { WireArticleFull } from '../api/types'

export default function LiveArticlePreviewPage() {
  const { id } = useParams<{ id: string }>()
  const [article, setArticle] = useState<WireArticleFull | null>(null)

  useEffect(() => {
    if (!id) return
    const channel = new BroadcastChannel(`wire-ui:article-preview:${id}`)

    channel.onmessage = (ev: MessageEvent) => {
      if (ev.data?.type === 'state') {
        setArticle(ev.data.article)
      }
    }

    channel.postMessage({ type: 'ready' })

    return () => channel.close()
  }, [id])

  if (!article) {
    return (
      <div className="flex h-screen items-center justify-center bg-canvas">
        <div className="flex flex-col items-center gap-4 text-center">
          <Loader2 size={24} className="animate-spin text-fg-secondary" />
          <p className="text-sm font-semibold text-fg">Waiting for editor…</p>
          <p className="max-w-xs text-xs text-fg-secondary">
            Keep the editor tab open. This window will update live as you make changes.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen flex-col bg-canvas">
      {/* Preview bar */}
      <div className="flex shrink-0 items-center justify-between border-b border-line bg-accent-tertiary-fill px-6 py-2">
        <div className="flex items-center gap-3">
          <span className="text-xs font-bold uppercase tracking-widest text-fg-secondary">Article Preview</span>
          <span className="truncate text-xs text-fg-secondary">— {article.title}</span>
        </div>
        <button
          type="button"
          onClick={() => window.close()}
          className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-xs font-semibold text-fg transition-colors hover:bg-fill"
        >
          <X size={12} aria-hidden="true" />
          Close Window
        </button>
      </div>

      {/* Reader shell */}
      <div className="flex-1 overflow-y-auto">
        <Navbar />
        <ArticleRenderer article={article} />
      </div>
    </div>
  )
}
